import { Collection, Database, Q } from '@nozbe/watermelondb';

import Organisation from './Organisation';
import { ProcedureModel, ProcedurePayload } from '../../types/Procedures';
import {
    DBServiceOptionsWithImagesAndValidation,
    ProcedureDBServiceOptions,
} from '../../types/dbService';
import {
    addPreparedDataBatch,
    mapRecord,
    mapRecords,
    PreparedProcedureData,
} from './helpers/procedure/common';
import { validateEntityRecords } from './helpers/common/validateEntityRecords';
import {
    logAddProcedureActions,
    prepareCreateProcedure,
    prepareProcedureUserUpdate,
} from './helpers/procedure/procedureAdd';
import {
    logUpdateProcedureActions,
    prepareUpdateEntryProcedures,
    prepareUpdateProcedure,
} from './helpers/procedure/procedureUpdate';
import {
    logDeleteProcedureActions,
    prepareDeleteEntryProcedures,
    prepareDeleteProcedure,
} from './helpers/procedure/procedureDelete';

import { UserModel, UserPayload } from '../../types/User';

class Procedure {
    private database: Database;
    private collection: Collection<ProcedureModel>;
    private table = 'procedures';
    private options: ProcedureDBServiceOptions;

    constructor(options: ProcedureDBServiceOptions) {
        this.database = options.database;
        this.collection = options.database.collections.get(this.table);
        this.options = options;
    }

    getAll() {
        return this.collection.query();
    }

    getByID(id: string) {
        return this.collection.find(id);
    }

    findAndObserveById(id: string) {
        return this.collection.findAndObserve(id);
    }

    getByParam(param: string, value: any) {
        return this.collection.query(Q.where(param, value));
    }

    async add({
        payload,
        userId,
        userPayload,
    }: {
        payload: ProcedurePayload;
        userId: string;
        userPayload?: UserPayload;
    }) {
        const organisationService = new Organisation({
            database: this.database,
            imageService: this.options.imageService,
            logDBAction: this.options.logDBAction,
            validation: this.options.validation,
        });

        const organisation = await organisationService.get();
        const { id: organisationID } = organisation[0];

        return await this.database.write(async () => {
            const preparedData: PreparedProcedureData[] = [];
            const createdProcedure = prepareCreateProcedure({
                payload,
                userId,
                organisationID,
                collection: this.collection,
            });
            preparedData.push(mapRecord(createdProcedure, 'procedures', 'add'));

            let userToUpdate: UserModel | undefined;
            if (userPayload) {
                const user = await this.database.collections
                    .get<UserModel>('users')
                    .find(userId);
                userToUpdate = prepareProcedureUserUpdate(user);
                if (userToUpdate) {
                    preparedData.push(
                        mapRecord(userToUpdate, 'users', 'update'),
                    );
                }
            }

            validateEntityRecords(preparedData, this.options.validation);
            await addPreparedDataBatch(preparedData, this.database);

            logAddProcedureActions({
                procedure: createdProcedure,
                user: userToUpdate,
                logDBAction: this.options.logDBAction,
                tableName: this.table,
            });

            return createdProcedure;
        });
    }

    async update(
        id: string,
        payload: Partial<ProcedurePayload>,
        updateEntriesRelatedProceduresPrice = true,
    ) {
        const procedureElement = await this.getByID(id);
        const procedureEntries = procedureElement.entries
            ? await procedureElement.entries.fetch()
            : [];
        const serviceOptions: DBServiceOptionsWithImagesAndValidation = {
            database: this.database,
            imageService: this.options.imageService,
            logDBAction: this.options.logDBAction,
            validation: this.options.validation,
        };

        return await this.database.write(async () => {
            const preparedData: PreparedProcedureData[] = [];
            const updatedProcedureElement = prepareUpdateProcedure({
                procedureElement,
                payload,
            });
            preparedData.push(
                mapRecord(updatedProcedureElement, 'procedures', 'update'),
            );

            const entryProceduresToUpdate =
                procedureEntries.length > 0
                    ? await prepareUpdateEntryProcedures({
                          procedureId: id,
                          procedureElement,
                          payload,
                          updateEntriesRelatedProceduresPrice,
                          serviceOptions,
                      })
                    : [];
            preparedData.push(
                ...mapRecords(
                    entryProceduresToUpdate,
                    'entry_procedures',
                    'update',
                ),
            );

            validateEntityRecords(preparedData, this.options.validation);
            await addPreparedDataBatch(preparedData, this.database);

            logUpdateProcedureActions({
                procedure: updatedProcedureElement,
                entryProceduresToUpdate,
                logDBAction: this.options.logDBAction,
                tableName: this.table,
            });

            return updatedProcedureElement;
        });
    }

    async deleteByID(id: string) {
        const procedureElement = await this.getByID(id);
        const procedureEntries = procedureElement.entries
            ? await procedureElement.entries.fetch()
            : [];
        const serviceOptions: DBServiceOptionsWithImagesAndValidation = {
            database: this.database,
            imageService: this.options.imageService,
            logDBAction: this.options.logDBAction,
            validation: this.options.validation,
        };

        await this.database.write(async () => {
            const preparedData: PreparedProcedureData[] = [];
            const procedureToDelete = prepareDeleteProcedure({
                procedure: procedureElement,
            });
            preparedData.push(
                mapRecord(procedureToDelete, 'procedures', 'delete'),
            );

            const entryProceduresToDelete =
                procedureEntries.length > 0
                    ? await prepareDeleteEntryProcedures({
                          procedureId: id,
                          serviceOptions,
                      })
                    : [];
            preparedData.push(
                ...mapRecords(
                    entryProceduresToDelete,
                    'entry_procedures',
                    'delete',
                ),
            );

            validateEntityRecords(preparedData, this.options.validation);
            await addPreparedDataBatch(preparedData, this.database);

            logDeleteProcedureActions({
                procedure: procedureToDelete,
                entryProceduresToDelete,
                logDBAction: this.options.logDBAction,
                tableName: this.table,
            });
        });
    }
}

export default Procedure;
